import {
  ensureStageRowId,
  ensureStageSessionForUser,
  fetchGameStageRecord,
  isLevelScoreRecorded,
} from "../../functions/stageReportSync";
import { GAMES } from "./gameConfig";

export function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

/** Demo sessions skip DB saves and end on the plans page. */
export function isDemoUser(storedUser) {
  const role = String(storedUser?.role || "").toLowerCase();
  const source = String(storedUser?.source || "").toUpperCase();
  const sessionId = String(storedUser?.sessionId || "");
  return (
    sessionId === "demobypass" ||
    role === "demobypass" ||
    source === "DEMO" ||
    sessionId.startsWith("demo")
  );
}

export function getBackendBase() {
  return String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
}

export function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

export function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

/** Sum of the six game scores on a stages row. */
export function sumGameScores(stage, exceptLevel = null) {
  if (!stage) return 0;
  return GAMES.reduce((sum, g) => {
    if (g.level === exceptLevel) return sum;
    return sum + (Number(stage[`stage${g.level}`]) || 0);
  }, 0);
}

export function playedLevels(stage) {
  return GAMES.filter((g) => isLevelScoreRecorded(stage, g.level)).map(
    (g) => g.level
  );
}

/**
 * Resolve the player's stages row and load it (row id, elapsed time, scores).
 * Demo / signed-out sessions resolve to an empty record.
 */
export async function loadPlayerStage({
  backendBase,
  adminToken,
  storedUser,
  isDemoBypass,
}) {
  if (isDemoBypass || !backendBase || !adminToken) {
    return { stageId: "", elapsedSec: 0, stage: null };
  }

  ensureStageSessionForUser(storedUser);
  const stageId = await ensureStageRowId({
    backendBase,
    adminToken,
    storedUser,
    isDemoBypass,
  });
  if (!stageId) return { stageId: "", elapsedSec: 0, stage: null };

  const stage = await fetchGameStageRecord({
    backendBase,
    adminToken,
    storedUser,
    stageId,
  });

  return {
    stageId: String(stage?.id ?? stageId),
    elapsedSec: parseClockToSeconds(stage?.time),
    stage,
  };
}
